<template>
    <main v-if="contentIsReady" class="job-page">
        <JobPageHero />
        <SliceZone v-if="reinsuranceSlice" :slices="[reinsuranceSlice]" :components="components" />
        <JobPageProfiles />
        <SliceZone v-if="columnsSlices[0]" :slices="[columnsSlices[0]]" :components="components" />
        <JobPageTags />
        <SliceZone v-if="columnsSlices[1]" :slices="[columnsSlices[1]]" :components="components" />
        <JobPageBarometers />
        <SliceZone v-if="slices" :slices="slices" :components="components" />
        <JobPageTips v-else-if="hasBothTips" :page="landingPage" :title="tipsTitle.value!" :content="tips.value!" />
    </main>
</template>

<script setup lang="ts">
    import type {Ref} from 'vue';
    import {provide, ref} from 'vue';
    import {asText} from '@prismicio/client';
    import type {Slice} from '@prismicio/types';
    import {SliceZone} from '@prismicio/vue';
    import type {ColumnsSlice, JobPageConfigSlice, JobPageDocument, JobTemplateDocument, ReinsuranceSliceSlice} from '@prismicio-types';
    import type {CollectionPage, ListItem, Thing} from 'schema-dts';
    import {
        createError,
        useAsyncData,
        useHead,
        useLocale,
        useLocalePrefix,
        useLogger,
        useRequestURL,
        useRoute,
        useSeoMeta,
        useState,
        useStructuredData,
        useWebsitePageView,
    } from '#imports';
    import {safelyNavigateTo} from '@malt/utils';
    import type {
        LandingPageContentTextualContent,
        LandingPageContentWithHighlightedProfiles,
        LandingPageContentWithProfiles,
        TextContent,
    } from '@api';
    import {useAcquisitionPage} from '~/composables/acquisition/useAcquisitionPage';
    import {useABTestSlices} from '~/composables/useABTestSlices';
    import {JOB_PAGE} from '~/keys';
    import {components} from '~/slices/components';
    import JobPageBarometers from './JobPageBarometers.vue';
    import JobPageHero from './JobPageHero.vue';
    import JobPageProfiles from './JobPageProfiles.vue';
    import JobPageTags from './JobPageTags.vue';
    import JobPageTips from './JobPageTips.vue';

    const logger = useLogger();
    const route = useRoute();

    // template variables
    const contentIsReady = ref(false);
    let columnsSlices: Ref<ColumnsSlice[]>;
    let hasBothTips: boolean;
    let reinsuranceSlice: Ref<ReinsuranceSliceSlice>;
    let slices: any;
    let tips: TextContent;
    let tipsTitle: TextContent;

    // route params
    const p = route.params;
    const searchParams = {
        familyOrCity: (p.familyIdOrCityId || p.familyId) as string,
        categoryOrCity: (p.categoryIdOrCityId || p.categoryId) as string,
        subCategoryIdOrCity: (p.subCategoryIdOrCityId || p.subCategoryId) as string,
        city: p.cityId as string,
    };

    // fetch main
    const {getLocalePrefix, getLocaleCodeFromPrefix} = useLocalePrefix();
    const localePrefix = getLocalePrefix();
    const localeFromUrlPrefix = localePrefix ? getLocaleCodeFromPrefix(localePrefix) : undefined;
    const {fetchLandingPage, fetchPrismicDocument} = useAcquisitionPage({
        ...searchParams,
        locale: localeFromUrlPrefix,
    });
    const {data, error} = await useAsyncData('job-page', () => Promise.allSettled([fetchLandingPage(), fetchPrismicDocument('jobTemplate')]), {
        deep: true,
    });

    //  We check data length here to ensure we have both landing page and prismic template.
    if (!data.value || data.value.length !== 2 || data.value[0].status === 'rejected' || error.value) {
        const errorMessage = `JobsPage: error while fetching jobs page (landing): ${searchParams.familyOrCity} + ${searchParams.categoryOrCity} + ${searchParams.subCategoryIdOrCity} + ${searchParams.city}`;
        const newError = new Error(errorMessage, {
            cause: JSON.stringify({
                length: data.value?.length,
                landingPageStatus: data.value?.[0].status,
                PrismicDocumentStatus: data.value?.[1].status,
                maybeError: error.value,
            }),
        });
        logger.error(newError);
        throw createError({
            ...newError,
            statusCode: 404,
            statusMessage: errorMessage,
        });
    }

    const [landingPageOutcome, prismicTemplateOutcome] = data.value!;
    // we already ensured a few lines above that this promise's status was not 'rejected'
    const landingPage = (landingPageOutcome as PromiseFulfilledResult<LandingPageContentWithProfiles | LandingPageContentWithHighlightedProfiles>)
        .value;

    if (landingPage.newUrlToRedirect) {
        safelyNavigateTo(landingPage.newUrlToRedirect, {redirectCode: 301});
    } else if (prismicTemplateOutcome.status === 'rejected') {
        // In case we couldn't load a template from Prismic for this page (likely because we're using an unsupported
        // locale on malt.com, e.g. en_SK) an easy fallback for users (as it's not useful to SEO) is to redirect them
        // to an equivalent search result page. This is especially relevant as we will display links to category pages
        // on profile pages, without knowing if a Prismic template will exist to render them. This way, we avoid users
        // clicking on a profile category to be redirected to a 404 page.
        if (landingPage.seeMoreProfilesUrl) {
            safelyNavigateTo(landingPage.seeMoreProfilesUrl, {redirectCode: 302});
        } else {
            const errorMessage = `JobsPage: error while fetching jobs page (template): ${searchParams.familyOrCity} + ${searchParams.categoryOrCity} + ${searchParams.subCategoryIdOrCity} + ${searchParams.city}`;
            logger.error(new Error(errorMessage));
            throw createError({
                statusCode: 404,
                statusMessage: errorMessage,
            });
        }
    } else {
        const prismicTemplate = prismicTemplateOutcome.value;

        // fetch prismic page
        const uid = Object.values(searchParams)
            .filter((value) => value)
            .join('-');
        const {data: prismicPage} = await useAsyncData('job-page-prismic', () => fetchPrismicDocument('jobPage', uid), {deep: true});

        // slices
        const {slices: templateSlices} = useABTestSlices(prismicTemplate as JobTemplateDocument);
        const jobPageConfig = ref<JobPageConfigSlice>(templateSlices.find((slice: Slice) => slice.slice_type === 'job_page_config'));
        reinsuranceSlice = ref<ReinsuranceSliceSlice>(templateSlices.find((slice: Slice) => slice.slice_type === 'reinsurance_slice'));
        columnsSlices = ref<ColumnsSlice[]>(templateSlices.filter((slice: Slice) => slice.slice_type === 'columns'));

        provide(JOB_PAGE, {
            landingPage,
            prismicTemplate: prismicTemplate as JobTemplateDocument,
            prismicPage: prismicPage.value as JobPageDocument,
            jobPageConfig: jobPageConfig.value as JobPageConfigSlice,
        });

        // insert jobLabel in slice titles
        const jobLabel = landingPage?.textualContent?.thisPage.jobLabel as string;
        columnsSlices.value = columnsSlices.value.map((slice: ColumnsSlice) => {
            const text: string = asText(slice.primary.title);

            return {
                ...slice,
                primary: {
                    ...slice.primary,
                    title: slice.primary.title.map((title) => ({
                        ...title,
                        text: text.replace('{0}', jobLabel),
                    })),
                },
            } as ColumnsSlice;
        });
        slices = prismicPage.value?.data.slices;

        tipsTitle = landingPage.textualContent.tipsTitle;
        tips = landingPage.textualContent.tips;
        hasBothTips = !!(tipsTitle.value && tips.value);
        contentIsReady.value = true;

        // SEO
        useSeoMeta({
            title: landingPage.textualContent.thisPage.title,
            description: landingPage.textualContent.thisPage.meta,
        });
    }

    // Tracking
    useWebsitePageView('cms', 'job-categories');

    useSeoForJobPage(landingPage.textualContent);

    /**
     * Use this function to set the relevant data for SEO of a job page:
     * - Structured data (ld+json):
     * - Canonical URL
     * @param textualContent The textual content of the job page.
     */
    function useSeoForJobPage(textualContent: LandingPageContentTextualContent) {
        const {defineStructuredData} = useStructuredData();

        const {locale} = useLocale();

        const structuredData = useState<Thing>('malt-job-page-structured-data');

        // Canonical
        const {origin, pathname} = useRequestURL();
        const canonicalParams = route.query.p ? `?p=${route.query.p}` : '';
        const canonicalUrl = `${origin}${pathname}${canonicalParams}`;
        useHead({link: [{rel: 'canonical', href: canonicalUrl}]});

        // Structured data
        const additionalDataForThisPage: Partial<ListItem> = {
            ...(textualContent.thisPage.meta ? {description: textualContent.thisPage.meta} : {}),
        };

        structuredData.value = {
            '@type': 'CollectionPage',
            '@id': origin,
            name: textualContent.thisPage.title,
            description: textualContent.thisPage.meta,
            inLanguage: localeFromUrlPrefix ?? locale.value,
            breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: textualContent?.breadcrumbs.map((item, i) => {
                    const isLastItem = i === textualContent.breadcrumbs.length - 1;
                    return {
                        '@type': 'ListItem',
                        position: i + 1,
                        item: {
                            '@id': `${origin}/a${item.url}`,
                            name: item.label,
                            ...(isLastItem ? additionalDataForThisPage : {}),
                        },
                    };
                }),
            },
        } as CollectionPage;

        defineStructuredData('malt-job-page-structured-data', structuredData.value);
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .job-page {
        overflow: hidden;

        .slice-columns {
            margin: var(--joy-core-spacing-10) 0 0;
            gap: 0;
            row-gap: var(--joy-core-spacing-10);

            .card {
                padding: 0 var(--joy-core-spacing-6);
                background: none;
                border: none;
                width: 100%;

                @include mq.screen_sm(min) {
                    width: 33.33%;
                }
            }
            .card-image img {
                height: 84px;
                max-width: 150px;
            }
            .card-title {
                color: var(--joy-color-text-heading);
                margin-top: var(--joy-core-spacing-6);
                font-family: var(--joy-font-family-title);
                font-size: var(--joy-font-size-primary-800);

                @include mq.screen_sm(min) {
                    font-size: var(--joy-font-size-secondary-200);
                }
            }
            .card-body {
                margin-top: var(--joy-core-spacing-1);
            }
        }
    }
</style>
